import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  getAllPatientsAdmin,
  createPatientAdmin,
  updatePatientAdmin,
  deactivatePatientAdmin,
  BLOOD_GROUPS,
} from '../services/adminApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const EMPTY_FORM = {
  phoneNumber: '',
  fullName: '',
  gender: '',
  email: '',
  dateOfBirth: '',
  age: '',
  bloodGroup: '',
};

const GENDERS = ['MALE', 'FEMALE', 'OTHER'];

const computeAge = (dobISO) => {
  if (!dobISO) return '';
  const dob = new Date(dobISO);
  if (isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 0 ? String(age) : '';
};

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'success' ? '#eaf3de' : type === 'error' ? '#fcebeb' : '#faeeda',
    color: type === 'success' ? '#3b6d11' : type === 'error' ? '#a32d2d' : '#854f0b',
    border: `0.5px solid ${type === 'success' ? '#97c459' : type === 'error' ? '#f09595' : '#fac775'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s',
    pointerEvents: 'none',
    maxWidth: 360,
  }}>
    {message}
  </div>
);

// ---------------------------------------------------------------------------
// Confirm deactivate modal
// ---------------------------------------------------------------------------
const ConfirmModal = ({ patient, onConfirm, onCancel, deactivating }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="card-elevated modal-inner" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: '#fef2f2', border: '1px solid #fecaca',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, margin: '0 auto 14px',
        }}>🚫</div>
        <h3 className="modal-title" style={{ fontSize: 17 }}>Deactivate patient?</h3>
        <p className="modal-sub" style={{ marginTop: 6 }}>
          <strong style={{ color: '#1e1b4b' }}>{patient?.fullName}</strong> will no longer be able to log in.
          This can be reversed later by an admin.
        </p>
      </div>
      <div className="modal-actions">
        <button onClick={onCancel} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deactivating}
          style={{
            flex: 2, padding: '12px', fontSize: 14, fontWeight: 600,
            borderRadius: 10, cursor: deactivating ? 'not-allowed' : 'pointer',
            border: 'none', background: '#dc2626', color: '#fff',
            opacity: deactivating ? 0.6 : 1, transition: 'opacity .15s',
            fontFamily: 'DM Sans, sans-serif',
          }}>
          {deactivating ? 'Deactivating…' : 'Deactivate'}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Patient create/edit modal
// ---------------------------------------------------------------------------
const PatientModal = ({ patient, onSave, onClose }) => {
  const isEdit = !!patient?.id;
  const [form, setForm] = useState(
    isEdit
      ? {
          phoneNumber: patient.phone || '',
          fullName: patient.fullName || '',
          gender: patient.gender || '',
          email: patient.email || '',
          dateOfBirth: patient.dateOfBirth || '',
          age: patient.age?.toString() || '',
          bloodGroup: patient.bloodGroup || '',
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, [key]: val };
      // Auto-compute age from DOB whenever DOB changes
      if (key === 'dateOfBirth') {
        const computed = computeAge(val);
        if (computed !== '') next.age = computed;
      }
      return next;
    });
    if (errors[key]) setErrors(e2 => ({ ...e2, [key]: null }));
  };

  const validate = () => {
    const errs = {};

    if (!form.phoneNumber.trim()) {
      errs.phoneNumber = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(form.phoneNumber.trim().replace(/^\+91/, ''))) {
      errs.phoneNumber = 'Enter a valid 10-digit Indian mobile number';
    }

    if (!form.fullName.trim()) {
      errs.fullName = 'Full name is required';
    } else if (!/^[A-Za-z][A-Za-z\s.'-]{1,99}$/.test(form.fullName.trim())) {
      errs.fullName = 'Name should only contain letters, spaces, and . \' -';
    }

    if (!form.gender) errs.gender = 'Gender is required';

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = 'Enter a valid email address';
    }

    if (form.dateOfBirth) {
      const dob = new Date(form.dateOfBirth);
      const today = new Date();
      if (isNaN(dob.getTime())) {
        errs.dateOfBirth = 'Enter a valid date';
      } else if (dob > today) {
        errs.dateOfBirth = 'Date of birth cannot be in the future';
      } else {
        const oldestAllowed = new Date();
        oldestAllowed.setFullYear(oldestAllowed.getFullYear() - 120);
        if (dob < oldestAllowed) errs.dateOfBirth = 'Enter a realistic date of birth';
      }
    }

    if (form.age !== '') {
      const ageNum = Number(form.age);
      if (!Number.isInteger(ageNum) || ageNum < 0 || ageNum > 120) {
        errs.age = 'Age must be between 0 and 120';
      } else if (form.dateOfBirth) {
        const computed = computeAge(form.dateOfBirth);
        if (computed !== '' && Math.abs(Number(computed) - ageNum) > 1) {
          errs.age = `Doesn't match date of birth (≈${computed})`;
        }
      }
    }

    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const payload = {
      phoneNumber: form.phoneNumber.trim(),
      fullName: form.fullName.trim(),
      gender: form.gender,
      email: form.email.trim() || null,
      dateOfBirth: form.dateOfBirth || null,
      age: form.age !== '' ? parseInt(form.age, 10) : null,
      bloodGroup: form.bloodGroup || null,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto', padding: '28px 28px 24px',
        boxShadow: '0 24px 64px rgba(79,70,229,0.18)',
        animation: 'fadeUp .25s ease both',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h2 className="modal-title">{isEdit ? 'Edit patient' : 'Create patient account'}</h2>
            <p className="modal-sub">
              {isEdit ? 'Update this patient\'s profile' : 'The phone number becomes their OTP login credential'}
            </p>
          </div>
          <button onClick={onClose} className="btn-close-x" style={{ fontSize: 22 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Phone */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Phone number <span className="required-star">*</span></label>
            <input
              value={form.phoneNumber}
              onChange={set('phoneNumber')}
              className={`input${errors.phoneNumber ? ' err' : ''}`}
              placeholder="+919123456789"
            />
            {errors.phoneNumber && <div className="field-error">{errors.phoneNumber}</div>}
            {isEdit && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Changing this updates their OTP login number</div>}
          </div>

          {/* Full name */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Full name <span className="required-star">*</span></label>
            <input
              value={form.fullName}
              onChange={set('fullName')}
              className={`input${errors.fullName ? ' err' : ''}`}
              placeholder="e.g. Rahul Mehta"
            />
            {errors.fullName && <div className="field-error">{errors.fullName}</div>}
          </div>

          {/* Gender */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Gender <span className="required-star">*</span></label>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              {GENDERS.map(g => (
                <label key={g} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: form.gender === g ? '#4f46e5' : '#374151' }}>
                  <input
                    type="radio"
                    name="gender"
                    checked={form.gender === g}
                    onChange={() => { setForm(f => ({ ...f, gender: g })); if (errors.gender) setErrors(e2 => ({ ...e2, gender: null })); }}
                  />
                  {g.charAt(0) + g.slice(1).toLowerCase()}
                </label>
              ))}
            </div>
            {errors.gender && <div className="field-error">{errors.gender}</div>}
          </div>

          {/* Email */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Email</label>
            <input type="email" value={form.email} onChange={set('email')} className={`input${errors.email ? ' err' : ''}`} placeholder="patient@example.com" />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>

          {/* DOB + Age */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Date of birth</label>
              <input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} max={new Date().toISOString().slice(0, 10)} className={`input${errors.dateOfBirth ? ' err' : ''}`} />
              {errors.dateOfBirth && <div className="field-error">{errors.dateOfBirth}</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">
                Age
                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>(auto from DOB)</span>
              </label>
              <input type="number" min="0" max="120" value={form.age} onChange={set('age')} className={`input${errors.age ? ' err' : ''}`} placeholder="e.g. 34" />
              {errors.age && <div className="field-error">{errors.age}</div>}
            </div>
          </div>

          {/* Blood group */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Blood group</label>
            <select value={form.bloodGroup} onChange={set('bloodGroup')} className="input">
              <option value="">Select…</option>
              {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions" style={{ marginTop: 22 }}>
          <button onClick={onClose} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 2, padding: '12px', fontSize: 14, fontWeight: 600,
              borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer',
              border: 'none', background: saving ? '#c7d2fe' : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              color: '#fff', opacity: saving ? 0.8 : 1, transition: 'opacity .15s',
              fontFamily: 'DM Sans, sans-serif',
            }}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create patient'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const AdminPatients = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { tenant } = useTenant();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');

  const [editPatient, setEditPatient]           = useState(null); // patient or {} for new
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating]         = useState(false);

  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const showToast = useCallback((message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3200);
  }, []);

  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAllPatientsAdmin(token, tenant.id);
      setPatients(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editPatient?.id) {
        await updatePatientAdmin(token, tenant.id, editPatient.id, payload);
        showToast('Patient updated');
      } else {
        await createPatientAdmin(token, tenant.id, payload);
        showToast(`Patient account created. They can now log in with ${payload.phoneNumber} via OTP.`);
      }
      setEditPatient(null);
      load();
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await deactivatePatientAdmin(token, tenant.id, deactivateTarget.id);
      showToast('Patient deactivated');
      setDeactivateTarget(null);
      load();
    } catch (e) {
      showToast(`Deactivation failed: ${e.message}`, 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const filtered = patients.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (p.fullName || '').toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Manage Patients</span>
        </div>

        <div className="fade-up" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>🧑‍🤝‍🧑 Manage Patients</h1>
            <p className="t-body-sm">
              {!loading && !error && `${patients.length} patient${patients.length !== 1 ? 's' : ''} total`}
            </p>
          </div>
          <button
            onClick={() => setEditPatient({})}
            style={{
              fontSize: 13.5, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              border: 'none', borderRadius: 12, padding: '10px 20px',
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              boxShadow: '0 4px 14px rgba(79,70,229,0.25)',
            }}
          >
            + Add Patient
          </button>
        </div>

        <div className="card fade-up-1" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search by name, phone, email…"
              style={{ height: 36, fontSize: 13 }}
            />
            {search && <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>}
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div className="card fade-up-2" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '10px 20px',
            background: '#f8f9ff', borderBottom: '1px solid #f3f4f6',
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif',
          }}>
            <span>Patient</span>
            <span>Actions</span>
          </div>

          <div style={{ padding: '0 20px' }}>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                    <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="skeleton skeleton-line" style={{ width: '35%', height: 13, marginBottom: 6 }} />
                      <div className="skeleton skeleton-line" style={{ width: '50%', height: 11 }} />
                    </div>
                  </div>
                ))
              : filtered.length === 0
                ? (
                  <div className="empty-state">
                    <div className="empty-icon">🧑‍🤝‍🧑</div>
                    <p className="empty-title">{search ? 'No patients match' : 'No patients yet'}</p>
                  </div>
                )
                : filtered.map((p, idx) => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 0', borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
                    animation: `fadeUp .3s ${idx * 20}ms ease both`,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #d97706DD, #d9770688)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'Sora, sans-serif',
                    }}>
                      {(p.fullName || '?').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>
                        {p.fullName}
                      </div>
                      <div className="t-meta">
                        {p.age != null && <span>{p.age} yrs</span>}
                        {p.gender && <span style={{ marginLeft: p.age != null ? 10 : 0 }}>{p.gender}</span>}
                        <span style={{ marginLeft: 10 }}>📱 {p.phone}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={() => setEditPatient(p)}
                        style={{
                          fontSize: 12, fontWeight: 600, color: '#4f46e5', background: '#eef2ff',
                          border: '1px solid #c7d2fe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeactivateTarget(p)}
                        style={{
                          fontSize: 12, fontWeight: 600, color: '#dc2626', background: '#fef2f2',
                          border: '1px solid #fecaca', borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        Deactivate
                      </button>
                    </div>
                  </div>
                ))
            }
          </div>
        </div>

      </main>

      {editPatient && (
        <PatientModal
          patient={editPatient}
          onSave={handleSave}
          onClose={() => setEditPatient(null)}
        />
      )}

      {deactivateTarget && (
        <ConfirmModal
          patient={deactivateTarget}
          onConfirm={handleDeactivate}
          onCancel={() => setDeactivateTarget(null)}
          deactivating={deactivating}
        />
      )}

      <Toast {...toast} />
    </div>
  );
};

export default AdminPatients;